import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { isTerminalJob, type JobRunEntry, type JobRunStatus } from '@/features/notifications/NotificationTypes';
import { OnboardingService, type OnboardingSignals } from './OnboardingService';

const POLL_INTERVAL_MS = 3_000;

/** A checklist row on the onboarding page. */
export interface OnboardingStep {
    key: string;
    label: string;
    description: string;
    done: boolean;
    optional: boolean;
    href: string;
}

/** Options for the final "bring your data to life" run, decided by the page from plan features. */
export interface StartPipelineOptions {
    providerConnectionId?: string;
    directoryConnectionId?: string;
    /** Only run usage-pull/classify/insights when the plan includes insights. */
    runInsights: boolean;
}

type RowStatus = JobRunStatus | 'pending';

/** One row in the final live job tracker. */
export interface TrackedRow {
    kind: string;
    label: string;
    status: RowStatus;
    message: string | null;
    progress: number | null;
    total: number | null;
}

interface ExpectedJob {
    kind: string;
    label: string;
    jobRunId?: string;
}

const KIND_LABELS: Record<string, string> = {
    'usage-backfill': 'Importing your usage',
    classification: 'Classifying your usage',
    insights: 'Building your insights',
    'license-sync': 'Syncing licenses',
    'directory-sync': 'Syncing your directory',
};

@injectable()
export class OnboardingViewModel extends BaseViewModel {
    signals: OnboardingSignals | null = null;
    loading = false;
    error: string | null = null;

    phase: 'checklist' | 'running' = 'checklist';
    starting = false;
    private jobs: JobRunEntry[] = [];
    private expected: ExpectedJob[] = [];
    private triggerErrors = new Map<string, string>();
    private kickoffTime = '';
    private lastOptions: StartPipelineOptions | null = null;
    private pollTimer: ReturnType<typeof setInterval> | null = null;

    constructor(private readonly service: OnboardingService) {
        super();
    }

    async load(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            this.signals = await this.service.loadSignals();
        } catch (e) {
            this.error = e instanceof Error ? e.message : 'Failed to load onboarding';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    get steps(): OnboardingStep[] {
        const s = this.signals;
        const providerDone = (s?.providerConnections.length ?? 0) > 0;
        const identityDone = (s?.directoryConnections.length ?? 0) > 0;
        const teamDone = (s?.userCount ?? 0) > 1;
        const cardDone = !!s?.overview?.card;
        const steps: OnboardingStep[] = [
            { key: 'provider', label: 'Connect your first provider', description: 'Link an AI usage provider so we can pull activity.', done: providerDone, optional: false, href: '/settings/providers' },
            { key: 'identity', label: 'Connect an identity provider', description: 'Sync your directory to map usage to people and teams.', done: identityDone, optional: true, href: '/settings/identity-providers' },
            { key: 'team', label: 'Invite your team', description: 'Bring the people who will use Ultra Trace.', done: teamDone, optional: true, href: '/settings/users' },
        ];
        if (this.isSaas) {
            steps.push({ key: 'payment', label: 'Add a payment method', description: 'Save a card to enable your subscription.', done: cardDone, optional: true, href: '/settings/billing' });
        }
        return steps;
    }

    get isSaas(): boolean {
        return this.signals?.overview?.provider === 'stripe';
    }

    get providerConnected(): boolean {
        return (this.signals?.providerConnections.length ?? 0) > 0;
    }

    async startPipeline(options: StartPipelineOptions): Promise<void> {
        this.starting = true;
        this.lastOptions = options;
        this.expected = [];
        this.triggerErrors.clear();
        this.jobs = [];
        this.kickoffTime = new Date().toISOString();
        this.notifyChange();

        if (options.providerConnectionId && options.runInsights) {
            try {
                const ack = await this.service.startFirstRun(options.providerConnectionId);
                const byKind = new Map(ack.jobs.map((j) => [j.kind, j.jobRunId]));
                for (const kind of ['usage-backfill', 'classification', 'insights']) {
                    const jobRunId = byKind.get(kind);
                    this.expected.push({ kind, label: KIND_LABELS[kind] ?? kind, ...(jobRunId ? { jobRunId } : {}) });
                }
            } catch (e) {
                for (const kind of ['usage-backfill', 'classification', 'insights']) {
                    this.expected.push({ kind, label: KIND_LABELS[kind] ?? kind });
                    this.triggerErrors.set(kind, e instanceof Error ? e.message : 'Failed to start');
                }
            }
        }

        if (options.providerConnectionId) {
            this.expected.push({ kind: 'license-sync', label: KIND_LABELS['license-sync']! });
            await this.service.startLicenseSync(options.providerConnectionId).catch((e: unknown) => {
                this.triggerErrors.set('license-sync', e instanceof Error ? e.message : 'Sync failed');
            });
        }

        if (options.directoryConnectionId) {
            this.expected.push({ kind: 'directory-sync', label: KIND_LABELS['directory-sync']! });
            await this.service.startDirectorySync(options.directoryConnectionId).catch((e: unknown) => {
                this.triggerErrors.set('directory-sync', e instanceof Error ? e.message : 'Sync failed');
            });
        }

        this.phase = 'running';
        this.starting = false;
        this.notifyChange();
        this.startPolling();
    }

    async retry(): Promise<void> {
        if (this.lastOptions) await this.startPipeline(this.lastOptions);
    }

    complete(): Promise<void> {
        return this.service.complete();
    }

    dismiss(): Promise<void> {
        return this.service.dismiss();
    }

    get rows(): TrackedRow[] {
        return this.expected.map((e) => this.resolveRow(e));
    }

    get allComplete(): boolean {
        return this.expected.length > 0 && this.expected.every((e) => isTerminalRow(this.resolveRow(e).status));
    }

    get anyFailed(): boolean {
        return this.rows.some((r) => r.status === 'failed');
    }

    dispose(): void {
        this.stopPolling();
        super.dispose();
    }

    private startPolling(): void {
        this.stopPolling();
        void this.pollJobs();
        this.pollTimer = setInterval(() => { void this.pollJobs(); }, POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private async pollJobs(): Promise<void> {
        try {
            this.jobs = await this.service.listJobs();
            this.notifyChange();
            if (this.allComplete) this.stopPolling();
        } catch {
            void 0;
        }
    }

    private resolveRow(exp: ExpectedJob): TrackedRow {
        const job = exp.jobRunId
            ? this.jobs.find((j) => j.jobRunId === exp.jobRunId)
            : this.jobs
                  .filter((j) => j.kind === exp.kind && j.startedAt >= this.kickoffTime)
                  .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1))[0];
        if (job) {
            return { kind: exp.kind, label: exp.label, status: job.status, message: job.message, progress: job.progress, total: job.total };
        }
        const err = this.triggerErrors.get(exp.kind);
        if (err) return { kind: exp.kind, label: exp.label, status: 'failed', message: err, progress: null, total: null };
        return { kind: exp.kind, label: exp.label, status: 'pending', message: null, progress: null, total: null };
    }
}

function isTerminalRow(status: RowStatus): boolean {
    return status !== 'pending' && isTerminalJob(status);
}
